import { getLocalStorageIfAvailable } from '../utils/web/storageAvailability';

export interface StorageAdapter {
    get(keys: string[]): Promise<Record<string, unknown>>;
    set(items: Record<string, unknown>): Promise<void>;
    remove(keys: string[]): Promise<void>;
}

export interface ConfigData {
    api_endpoint?: string; // Per-site API endpoint override (replaces inboundx_api_url)
    inboundx_evaluation_enabled?: boolean;
    inboundx_related_enabled?: boolean;
    inboundx_api_key?: string; // Still needed for backward compatibility in storage
}

export class WebStorageAdapter implements StorageAdapter {
    async get(keys: string[]): Promise<Record<string, unknown>> {
        const result: Record<string, unknown> = {};
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return result;

        keys.forEach((key) => {
            try {
                const value = localStorageRef.getItem(key);
                if (value !== null) {
                    try {
                        result[key] = JSON.parse(value);
                    } catch {
                        result[key] = value;
                    }
                }
            } catch {
                // Some WebViews block storage reads even when localStorage exists.
            }
        });
        return result;
    }

    async set(items: Record<string, unknown>): Promise<void> {
        const localStorageRef = getLocalStorageIfAvailable();
        // Keep widget initialization resilient in WebView/privacy contexts.
        if (!localStorageRef) return;

        Object.entries(items).forEach(([key, value]) => {
            try {
                localStorageRef.setItem(key, JSON.stringify(value));
            } catch {
                // Ignore storage write failures and continue with runtime defaults.
            }
        });
    }

    async remove(keys: string[]): Promise<void> {
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return;

        keys.forEach((key) => {
            try {
                localStorageRef.removeItem(key);
            } catch {
                // Ignore storage removal failures and continue.
            }
        });
    }
}

interface ChromeLocalStorage {
    get(keys: string[], callback: (result: Record<string, unknown>) => void): void;
    set(items: Record<string, unknown>, callback: () => void): void;
    remove(keys: string[], callback: () => void): void;
}

interface ChromeWithStorage {
    storage?: { local: ChromeLocalStorage };
}

function getChromeLocalStorage(): ChromeLocalStorage {
    const chromeGlobal =
        typeof chrome === 'undefined' ? undefined : (chrome as unknown as ChromeWithStorage);
    const local = chromeGlobal?.storage?.local;
    if (!local) {
        throw new Error('Chrome storage not available');
    }
    return local;
}

export class ChromeStorageAdapter implements StorageAdapter {
    async get(keys: string[]): Promise<Record<string, unknown>> {
        const local = getChromeLocalStorage();
        return new Promise((resolve) => {
            local.get(keys, resolve);
        });
    }

    async set(items: Record<string, unknown>): Promise<void> {
        const local = getChromeLocalStorage();
        return new Promise((resolve) => {
            local.set(items, resolve);
        });
    }

    async remove(keys: string[]): Promise<void> {
        const local = getChromeLocalStorage();
        return new Promise((resolve) => {
            local.remove(keys, resolve);
        });
    }
}
